import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Row, Col } from 'react-bootstrap';
import CustomModal from '../CustomModal';
import Input from '../input';
import Button from '../button';
import SpinnerLoader from '../../utils/SpinnerLoader';
import { apiService } from '../../services/apiService';
import { successNotify, errorNotify } from '../../utils/toast';

const FuelRequestModal = ({
    show,
    onClose,
    requestId = null,
    tripId,
    tripCode,
    vehicleId,
    vehicleName,
    onSuccess,
    allocatedFuel,
}) => {
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    useEffect(() => {
        if (!show) {
            setDetail(null);
            return;
        }

        if (requestId) {
            setLoadingDetail(true);
            apiService.getFuelRequestDetail(requestId)
                .then((res) => {
                    const item = res?.data || res || {};
                    setDetail(item);
                })
                .catch((err) => {
                    errorNotify(err || 'Failed to load fuel request details');
                })
                .finally(() => {
                    setLoadingDetail(false);
                });
        } else {
            setDetail(null);
        }
    }, [show, requestId]);

    const isEditMode = Boolean(requestId || detail?.id);

    const effectiveAllocatedFuel = detail?.trip?.allocated_fuel ?? allocatedFuel ?? null;

    const fuelRequestValidationSchema = useMemo(() => {
        let qtySchema = Yup.number()
            .typeError('Requested fuel quantity must be a number')
            .positive('Requested fuel quantity must be greater than 0')
            .required('Requested fuel quantity is required');

        const limit = Number(effectiveAllocatedFuel);
        if (limit > 0) {
            qtySchema = qtySchema.max(limit, `Cannot exceed allocated fuel (${limit} ltrs)`);
        }

        return Yup.object().shape({
            requested_fuel_quantity: qtySchema,
            remarks: Yup.string().nullable(),
        });
    }, [effectiveAllocatedFuel]);

    const initialValues = {
        trip_id: detail?.trip_id || tripId || '',
        vehicle_id: detail?.vehicle_id || vehicleId || '',
        requested_fuel_quantity: detail?.requested_fuel_quantity !== undefined ? detail.requested_fuel_quantity : '',
        remarks: detail?.remarks || '',
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const numericTripId = Number(values.trip_id || detail?.trip_id || tripId);
            const numericVehicleId = Number(values.vehicle_id || detail?.vehicle_id || vehicleId);
            const numericQty = Number(values.requested_fuel_quantity);

            if (!numericTripId) {
                errorNotify('Trip ID is missing');
                setSubmitting(false);
                return;
            }

            if (!numericVehicleId) {
                errorNotify('Vehicle ID is missing for this trip');
                setSubmitting(false);
                return;
            }

            if (isEditMode) {
                const updatePayload = {
                    id: Number(requestId || detail?.id),
                    trip_id: numericTripId,
                    vehicle_id: numericVehicleId,
                    requested_fuel_quantity: numericQty,
                    remarks: values.remarks?.trim() || `Fuel request for trip #${detail?.trip?.trip_id || numericTripId}`,
                };

                await apiService.updateFuelRequest(updatePayload);
                successNotify('Fuel request updated successfully');
            } else {
                const createPayload = {
                    trip_id: numericTripId,
                    vehicle_id: numericVehicleId,
                    requested_fuel_quantity: numericQty,
                    remarks: values.remarks?.trim() || `Fuel request for long route trip #${tripCode || numericTripId}`,
                };

                await apiService.createFuelRequest(createPayload);
                successNotify('Fuel request submitted successfully');
            }

            resetForm();
            if (onClose) onClose();
            if (onSuccess) onSuccess();
        } catch (error) {
            errorNotify(error || (isEditMode ? 'Failed to update fuel request' : 'Failed to submit fuel request'));
        } finally {
            setSubmitting(false);
        }
    };

    const resolvedTripDisplay = detail?.trip?.trip_id
        ? detail.trip.trip_id
        : tripCode
            ? (String(tripCode).startsWith('TR-') ? tripCode : `TR-${tripCode}`)
            : (detail?.trip_id || tripId ? `TR-${detail?.trip_id || tripId}` : '--');

    const resolvedVehicleDisplay = detail?.vehicle?.registration_number
        ? detail.vehicle.registration_number.toUpperCase()
        : vehicleName || (detail?.vehicle_id || vehicleId ? `Vehicle #${detail?.vehicle_id || vehicleId}` : '--');

    return (
        <CustomModal
            heading={isEditMode ? `Edit Fuel Request ${requestId || detail?.id}` : 'Fuel Request'}
            size="md"
            show={show}
            handleClose={onClose}
        >
            {loadingDetail ? (
                <div style={{ padding: '40px 0', display: 'flex', justifyContent: 'center' }}>
                    <SpinnerLoader />
                </div>
            ) : (
                <Formik
                    initialValues={initialValues}
                    validationSchema={fuelRequestValidationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                        <Form>
                            <Row className="g-3">
                                <Col md={6} sm={12}>
                                    <Input
                                        label="Trip"
                                        name="trip_display"
                                        type="text"
                                        value={resolvedTripDisplay}
                                        disabled={true}
                                        readOnly={true}
                                        required={false}
                                    />
                                </Col>
                                <Col md={6} sm={12}>
                                    <Input
                                        label="Vehicle"
                                        name="vehicle_display"
                                        type="text"
                                        value={resolvedVehicleDisplay}
                                        disabled={true}
                                        readOnly={true}
                                        required={false}
                                    />
                                </Col>
                                <Col md={12}>
                                    <Input
                                        label={`Requested Fuel Quantity${effectiveAllocatedFuel > 0 ? ` (Max: ${effectiveAllocatedFuel} ltrs)` : ''}`}
                                        name="requested_fuel_quantity"
                                        type="number"
                                        placeholder="Enter fuel quantity (e.g. 50.5)"
                                        value={values.requested_fuel_quantity}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.requested_fuel_quantity}
                                        touched={touched.requested_fuel_quantity}
                                        showExtraText={true}
                                        extraText="ltrs"
                                        required={true}
                                    />
                                </Col>
                                <Col md={12}>
                                    <Input
                                        label="Remarks"
                                        name="remarks"
                                        type="text"
                                        placeholder="Enter remarks (e.g. Fuel request for long route...)"
                                        value={values.remarks}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.remarks}
                                        touched={touched.remarks}
                                        required={false}
                                    />
                                </Col>
                            </Row>

                            <div className="mt-4">
                                <Button
                                    cancelBtn={onClose}
                                    cancelBtnText="Cancel"
                                    cancelBtnDisabled={isSubmitting}
                                    submitText={isEditMode ? 'Update Request' : 'Submit Request'}
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                    isShowSubmitBtn={true}
                                    isShowCancel={true}
                                    type="submit"
                                    shouldRow={true}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            )}
        </CustomModal>
    );
};

export default FuelRequestModal;
